/**
 * Bringing a hash-linked section into view.
 *
 * Split out of the component because the interesting part is not the scroll, it is *what gets
 * scrolled*. This used to call `scrollTo` on `#main`, which on every page that mounts an
 * ExpandSection is not the scroll container — it is exactly as tall as its content, so the call
 * was a silent no-op and links like `/risk-management/control/x#CONTROL_actions` never moved.
 */

/** Distance left above the section, so its heading does not sit flush against the scroller's edge. */
export const HASH_SCROLL_OFFSET = 20;

/**
 * The nearest ancestor that actually scrolls — carries an overflow AND has somewhere to go.
 *
 * The second half matters as much as the first: an `overflow: auto` wrapper that is taller than
 * its content scrolls nowhere, and stopping at it would leave the real scroller untouched.
 */
export const scrollParentOf = (node: HTMLElement): HTMLElement | null => {
  for (let el = node.parentElement; el; el = el.parentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
  }
  return null;
};

/**
 * Where `container` must be scrolled to for `element` to sit at its top edge.
 *
 * Measured as a delta between two viewport rects, never from `offsetTop`: offsetTop is relative
 * to the nearest *positioned* ancestor — a card, on the control page — not to the scroller, so
 * the old sum was in the wrong coordinate space even when it did hit a real container.
 */
export const scrollTopFor = (element: HTMLElement, container: HTMLElement): number => {
  const delta = element.getBoundingClientRect().top - container.getBoundingClientRect().top;
  return container.scrollTop + delta - HASH_SCROLL_OFFSET;
};

/**
 * Scroll `element` into view inside whichever ancestor scrolls.
 *
 * Falls back to `scrollIntoView` when nothing does — the document itself may be the scroller, and
 * a no-op is the one outcome a hash link must not produce.
 */
export const scrollSectionIntoView = (element: HTMLElement): void => {
  const container = scrollParentOf(element);
  if (!container) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  container.scrollTo({ top: scrollTopFor(element, container), behavior: 'smooth' });
};
